/**
 * @deprecated LEGACY — DO NOT USE.
 * Canonical schema: apps/server/src/db/schema.ts
 * This file must not be imported by server/runtime code.
 */
import { sql } from "drizzle-orm";
import { relations } from "drizzle-orm";
import { pgTable, text, varchar, timestamp, date, integer, boolean, jsonb, pgEnum, unique, index } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
// Enums
export const locationEnum = pgEnum("location", ["SALEM", "BOSTON", "METHUEN", "CAPE"]);
export const reportStatusEnum = pgEnum("report_status", ["processing", "completed", "failed"]);
export const reportTypeEnum = pgEnum("report_type", ["daily", "weekly", "monthly"]);
// Dictionary Tables
export const statusCodes = pgTable("status_codes", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    code: varchar("code", { length: 50 }).notNull().unique(),
    label: text("label").notNull(),
    description: text("description"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
});
export const adoptionLevels = pgTable("adoption_levels", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    code: varchar("code", { length: 10 }).notNull().unique(), // L1, L2, L3, L4
    label: text("label").notNull(),
    description: text("description"),
    sortOrder: integer("sort_order").notNull(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
});
export const behaviorSeverities = pgTable("behavior_severities", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    code: varchar("code", { length: 10 }).notNull().unique(), // 1A, 1B, 2A, 2B... 5B
    label: text("label").notNull(),
    description: text("description"),
    sortOrder: integer("sort_order").notNull(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
});
// Zone Capacity Tables
export const zoneCapacities = pgTable("zone_capacities", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    zoneCode: varchar("zone_code", { length: 50 }).notNull().unique(),
    areaLabel: text("area_label").notNull(),
    runs: integer("runs").default(0),
    stainless: integer("stainless").default(0),
    variety: integer("variety").default(0),
    other: integer("other").default(0),
    totalCapacity: integer("total_capacity").generatedAlwaysAs(sql `COALESCE(runs, 0) + COALESCE(stainless, 0) + COALESCE(variety, 0) + COALESCE(other, 0)`),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
});
// Zone capacity aliases for mapping alternate codes to canonical zones
export const zoneCapacityAliases = pgTable("zone_capacity_aliases", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    aliasCode: varchar("alias_code", { length: 50 }).notNull().unique(),
    zoneCode: varchar("zone_code", { length: 50 }).notNull().references(() => zoneCapacities.zoneCode),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
});
// Behavior issues tracking for animals with behavior problems
export const behaviorIssues = pgTable("behavior_issues", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    animalId: varchar("animal_id", { length: 50 }).notNull().references(() => animals.animalId),
    location: locationEnum("location").notNull(),
    issueType: varchar("issue_type", { length: 50 }).notNull(),
    severity: varchar("severity", { length: 10 }).references(() => behaviorSeverities.code),
    description: text("description"),
    status: varchar("status", { length: 20 }).notNull().default("OPEN"),
    openedAt: timestamp("opened_at").defaultNow().notNull(),
    closedAt: timestamp("closed_at"),
    reportId: varchar("report_id").references(() => reports.id),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
});
// Reports Table
export const reports = pgTable("reports", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    location: locationEnum("location").notNull(),
    reportDate: date("report_date").notNull(),
    type: reportTypeEnum("type").notNull().default("daily"),
    sourceFile: text("source_file"), // S3 key
    status: reportStatusEnum("status").notNull().default("processing"),
    diagnostics: jsonb("diagnostics").$type(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
    uniqueLocationDateType: unique().on(table.location, table.reportDate, table.type),
    reportDateIdx: index().on(table.reportDate),
}));
// Report Line Items Table
export const reportLineItems = pgTable("report_line_items", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    reportId: varchar("report_id").notNull().references(() => reports.id),
    // Zone information
    zoneCode: varchar("zone_code", { length: 50 }).notNull(),
    zoneLabel: text("zone_label"),
    // Identity
    animalId: varchar("animal_id", { length: 50 }).notNull(),
    name: text("name").notNull(),
    microchipId: varchar("microchip_id", { length: 50 }),
    // Classification
    species: varchar("species", { length: 20 }).notNull(),
    primaryBreed: text("primary_breed"),
    mixNote: text("mix_note"),
    color: text("color"),
    // Demographics
    sex: varchar("sex", { length: 20 }),
    ageDisplay: text("age_display"),
    weightLb: integer("weight_lb"),
    weightKg: integer("weight_kg"),
    // Intake
    intakeDate: date("intake_date"),
    intakeSource: text("intake_source"),
    // Status
    statusRaw: text("status_raw"),
    statusCode: varchar("status_code", { length: 50 }),
    // Adoption
    adoptionLevelRaw: text("adoption_level_raw"),
    adoptionLevelNorm: varchar("adoption_level_norm", { length: 10 }),
    // Behavior
    behaviorSeverityRaw: text("behavior_severity_raw"),
    behaviorSeverityCode: varchar("behavior_severity_code", { length: 10 }),
    // Notes
    medicalDx: jsonb("medical_dx").$type().default([]),
    careInstructions: jsonb("care_instructions").$type().default([]),
    assessmentNotes: text("assessment_notes"),
    // Meta
    pageIndex: integer("page_index"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
    reportAnimalIdx: index().on(table.reportId, table.animalId),
}));
// Animals Table
export const animals = pgTable("animals", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    animalId: varchar("animal_id", { length: 50 }).notNull().unique(),
    name: text("name").notNull(),
    microchipId: varchar("microchip_id", { length: 50 }),
    species: varchar("species", { length: 20 }).notNull(),
    primaryBreed: text("primary_breed"),
    color: text("color"),
    sex: varchar("sex", { length: 20 }),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
    animalIdIdx: index().on(table.animalId),
}));
// Animal Current Status Table
export const animalCurrents = pgTable("animal_currents", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    animalId: varchar("animal_id", { length: 50 }).notNull().unique().references(() => animals.animalId),
    location: locationEnum("location").notNull(),
    zoneCode: varchar("zone_code", { length: 50 }).notNull(),
    statusCode: varchar("status_code", { length: 50 }),
    adoptionLevelNorm: varchar("adoption_level_norm", { length: 10 }),
    behaviorSeverityCode: varchar("behavior_severity_code", { length: 10 }),
    lastSeenDate: date("last_seen_date").notNull(),
    intakeDate: date("intake_date"),
    outcomeDate: date("outcome_date"),
    outcomeReason: varchar("outcome_reason", { length: 100 }),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
    animalStatusIdx: index().on(table.animalId, table.statusCode),
}));
// Auth Tables
export const users = pgTable("users", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    email: text("email").notNull().unique(),
    username: text("username").notNull().unique(),
    passwordHash: text("password_hash").notNull(),
    isAdmin: boolean("is_admin").notNull().default(false),
    isActive: boolean("is_active").notNull().default(true),
    totpSecret: text("totp_secret"),
    totpEnabled: boolean("totp_enabled").notNull().default(false),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
    emailIdx: index().on(table.email),
}));
export const invites = pgTable("invites", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    email: text("email").notNull(),
    token: text("token").notNull().unique(),
    role: varchar("role", { length: 20 }).notNull().default("staff"),
    invitedBy: varchar("invited_by").references(() => users.id),
    usedAt: timestamp("used_at"),
    expiresAt: timestamp("expires_at").notNull(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
    tokenIdx: index().on(table.token),
    emailIdx: index().on(table.email),
}));
export const sessions = pgTable("sessions", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    userId: varchar("user_id").notNull().references(() => users.id),
    token: text("token").notNull().unique(),
    ipAddress: varchar("ip_address", { length: 45 }),
    userAgent: text("user_agent"),
    expiresAt: timestamp("expires_at").notNull(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
    tokenIdx: index().on(table.token),
    userIdx: index().on(table.userId),
}));
export const totpSecrets = pgTable("totp_secrets", {
    id: varchar("id").primaryKey().default(sql `gen_random_uuid()`),
    userId: varchar("user_id").notNull().unique().references(() => users.id),
    secret: text("secret").notNull(),
    backupCodes: jsonb("backup_codes").$type().notNull(),
    verifiedAt: timestamp("verified_at"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
});
// Relations
export const reportsRelations = relations(reports, ({ many }) => ({
    lineItems: many(reportLineItems),
}));
export const reportLineItemsRelations = relations(reportLineItems, ({ one }) => ({
    report: one(reports, {
        fields: [reportLineItems.reportId],
        references: [reports.id],
    }),
}));
export const animalsRelations = relations(animals, ({ one, many }) => ({
    currentStatus: one(animalCurrents),
}));
export const animalCurrentsRelations = relations(animalCurrents, ({ one }) => ({
    animal: one(animals, {
        fields: [animalCurrents.animalId],
        references: [animals.animalId],
    }),
}));
export const usersRelations = relations(users, ({ many, one }) => ({
    invitesSent: many(invites),
    sessions: many(sessions),
    totpSecret: one(totpSecrets),
}));
export const invitesRelations = relations(invites, ({ one }) => ({
    invitedBy: one(users, {
        fields: [invites.invitedBy],
        references: [users.id],
    }),
}));
export const sessionsRelations = relations(sessions, ({ one }) => ({
    user: one(users, {
        fields: [sessions.userId],
        references: [users.id],
    }),
}));
export const totpSecretsRelations = relations(totpSecrets, ({ one }) => ({
    user: one(users, {
        fields: [totpSecrets.userId],
        references: [users.id],
    }),
}));
// Insert schemas for validation
export const insertUserSchema = createInsertSchema(users).omit({
    id: true,
    createdAt: true,
    updatedAt: true,
});
export const insertReportSchema = createInsertSchema(reports).omit({
    id: true,
    createdAt: true,
    updatedAt: true,
});
export const insertReportLineItemSchema = createInsertSchema(reportLineItems).omit({
    id: true,
    createdAt: true,
    updatedAt: true,
});
export const insertAnimalSchema = createInsertSchema(animals).omit({
    id: true,
    createdAt: true,
    updatedAt: true,
});
export const insertInviteSchema = createInsertSchema(invites).omit({
    id: true,
    createdAt: true,
});
